import './index.css'
import ToolItem from './ToolItem'
import { buildSnippet } from './snippet'
import React, { useEffect, useState } from 'react'
import FloatingFeedback from '../../FloatingFeedback/FloatingFeedback'
import { track } from 'statsig'
import { STATSIG, PAGE } from 'statsig/events'
import { isHardMoneyLoanCustomer } from '../../Common'

const STEPS = [
    {
        n: '1',
        title: 'Copy the code',
        body: 'One tag per tool. Click Copy next to the tool you want.',
    },
    {
        n: '2',
        title: 'Paste it into a page',
        body: 'In your site editor (Webflow, WordPress…), add it to the page where the tool should appear.',
    },
    {
        n: '3',
        title: 'Publish & check',
        body: 'Publish your site and open the page — the tool loads right where you pasted it.',
    },
]

const TOOLS = [
    {
        id: 'affordability',
        name: 'Home Affordability Tool',
        pageHint: 'affordability',
        previewSrc: '/1.webp',
        description:
            'Visitors enter income, debt, and down payment to see the home price they can afford — then click “Get Approved Now” to start an application with you.',
    },
    {
        id: 'calculator',
        name: 'Mortgage Calculator',
        pageHint: 'mortgage calculator',
        previewSrc: '/2.webp',
        description:
            'A monthly payment calculator with a full cost breakdown — principal, interest, taxes, insurance — and an amortization view.',
    },
    {
        id: 'purchase',
        name: 'Mortgage Rate Search',
        pageHint: 'purchase',
        previewSrc: '/3.webp',
        description:
            'Guides buyers through purchase scenarios with live rate options, and hands interested visitors off to you as qualified leads.',
    },
]

// Only hard money lenders price these loans, so only they get the tool.
const HARD_MONEY_TOOL = {
    id: 'hardmoneyloancalculator',
    name: 'Hard Money Loan Rate Calculator',
    pageHint: 'hard money loan',
    previewSrc: '/4.webp',
    description:
        'Real estate investors pick Rehab, Bridge, or Ground-Up Construction, enter the deal, and see an estimated interest rate, loan amount, monthly payment, and cash to close — then sign up to apply with you.',
}

const PublicSiteIntegration = () => {
    const [feedbackOpen, setFeedbackOpen] = useState(false)
    const tools = isHardMoneyLoanCustomer() ? [HARD_MONEY_TOOL, ...TOOLS] : TOOLS

    useEffect(() => {
        track(STATSIG.COMMON.VIEW, PAGE.PUBLIC_SITE_INTEGRATION, { page: PAGE.PUBLIC_SITE_INTEGRATION })
    }, [])

    return (
        <div className="integration">
            <div className="integration-inner">
                <h1>Website Tools</h1>
                <p className="integration-desc">
                    Add free mortgage tools to your website to capture leads. Each tool is one line of code — paste it into any page on Webflow, WordPress, or a custom site, and the tool appears there, styled to fit your page.
                </p>

                <div className="integration-steps">
                    {STEPS.map((step, index) => (
                        <div
                            key={step.n}
                            className={`integration-step${index > 0 ? ' has-divider' : ''}`}
                        >
                            <div className="integration-step-n">{step.n}</div>
                            <div>
                                <div className="integration-step-title">{step.title}</div>
                                <div className="integration-step-body">{step.body}</div>
                            </div>
                        </div>
                    ))}
                </div>

                <div className="integration-tools">
                    {tools.map((tool) => (
                        <ToolItem
                            key={tool.id}
                            id={tool.id}
                            name={tool.name}
                            description={tool.description}
                            pageHint={tool.pageHint}
                            previewSrc={tool.previewSrc}
                            snippet={buildSnippet(tool.id)}
                        />
                    ))}
                </div>

                <div className="integration-footer">
                    <p>
                        Not sure how to add code to your site?{' '}
                        <a
                            href="https://help.webflow.com/hc/en-us/articles/33961357265299-Custom-code-in-head-and-body-tags"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Webflow guide
                        </a>
                        {' · '}
                        <a
                            href="https://wordpress.org/documentation/article/custom-html/"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            WordPress guide
                        </a>
                        {' · '}
                        or forward this page to whoever manages your website.
                    </p>
                    <button
                        type="button"
                        onClick={() => {
                            setFeedbackOpen(true)
                            track(STATSIG.COMMON.FEEDBACK, 'open', {
                                source: 'contact_support',
                            })
                        }}
                    >
                        Contact support
                    </button>
                </div>
            </div>

            <FloatingFeedback
                showFeedback={feedbackOpen}
                setShowFeedback={setFeedbackOpen}
                selectedProduct="Management"
                selectedFeature="Public site integration"
            />
        </div>
    )
}

export default PublicSiteIntegration
